import { ApiError } from './http';

/** The one line for a failure the app has no words for: offline, a server error, an unknown refusal. */
export const plainTrouble = 'That did not go through. Check your connection and try again.';

/** What a person reads when their role may not do the thing, in place of the API's scope sentence. */
export const roleRefusal = 'Your role cannot do that. Ask an admin of the company.';

/** The codes the API gives a problem that named none: its status's, which says nothing about who the detail was written for. */
const statusCodes = new Set([
  'invalid_request',
  'unauthenticated',
  'payment_required',
  'forbidden',
  'not_found',
  'method_not_allowed',
  'conflict',
  'gone',
  'too_large',
  'unsupported_media_type',
  'unprocessable',
  'rate_limited',
  'internal_error',
  'not_implemented',
  'bad_gateway',
  'unavailable',
  'timeout',
]);

/** True when the API named the refusal itself (`mail_failed`, `code_locked`): its detail is a sentence written for a person. */
export function hasOwnCode(error: ApiError): boolean {
  const code: string | null = error.code;
  return code !== null && !statusCodes.has(code) && !code.startsWith('http_');
}

/**
 * The line a customer reads for a failed request: the API's own sentence when it refused with one
 * written for a person (a 4xx problem's detail, or a failure the API named with a code of its own:
 * "Paige could not send the email just now"), `fallback` otherwise. Never the browser's text
 * ("Failed to fetch"), a server error's, or the API's wording about scopes and roles
 * (billing-refusals.ts does the same for card payments).
 */
export function plainFailure(error: unknown, fallback: string = plainTrouble): string {
  if (!(error instanceof ApiError) || !error.detail) return fallback;
  if (error.status >= 500 && !hasOwnCode(error)) return fallback;
  if (error.status === 403 && /^This action needs the /i.test(error.detail)) return roleRefusal;
  return error.detail;
}

/**
 * {@link plainFailure} for a request the page built itself (a link's values, a fixed body). A 400
 * there names the malformed field in a developer's words ("codeChallenge must be an S256 challenge"),
 * which is nothing the person typed, so the page's own line (`malformed`) stands in for it.
 */
export function plainUnlessMalformed(
  error: unknown,
  malformed: string,
  fallback: string = plainTrouble,
): string {
  if (error instanceof ApiError && (error.status === 400 || error.status === 422)) return malformed;
  return plainFailure(error, fallback);
}

/** {@link plainFailure}, or null when nothing failed. */
export function failureLine(error: unknown, fallback?: string): string | null {
  return error ? plainFailure(error, fallback) : null;
}
